/**
 * Ponto único de acoplamento da exceção comercial de take rate (PED-13773).
 *
 * Contratos de PO podem ser lançados com take rate abaixo de 10%, desde que o
 * operador informe a justificativa, o responsável e a data da aprovação.
 *
 * Contrato da API (PED-13772) — mesmo formato em POST /private-offices/contracts,
 * PATCH /private-offices/contracts/:id e PATCH .../bookings/:bookingId:
 *
 *   takeRateException: {
 *     justification: string;  // 1..1000 caracteres
 *     approvedBy: string;     // 1..255 caracteres
 *     approvedAt: string;     // ISO-8601
 *   }
 *
 * O objeto é obrigatório sempre que takePercentage < 10 for enviado (a API
 * responde 400/4014 se faltar qualquer um dos campos) e não deve ser enviado
 * quando o take rate é maior ou igual a 10.
 */

/** Abaixo deste valor o lançamento é uma exceção comercial. */
export const TAKE_RATE_EXCEPTION_THRESHOLD = 10;

/** Nomes dos campos no formulário (conform + zod). */
export const TAKE_RATE_EXCEPTION_FORM_FIELDS = {
  justification: "takeRateExceptionJustification",
  approvedBy: "takeRateExceptionApprovedBy",
  approvedAt: "takeRateExceptionApprovedAt",
} as const;

/** Chave do objeto enviado para a API e nomes dos seus campos. */
export const TAKE_RATE_EXCEPTION_API_FIELD = "takeRateException";
export const TAKE_RATE_EXCEPTION_API_FIELDS = {
  justification: "justification",
  approvedBy: "approvedBy",
  approvedAt: "approvedAt",
} as const;

/** Limites de tamanho exigidos pela API. */
export const TAKE_RATE_EXCEPTION_MAX_JUSTIFICATION_LENGTH = 1000;
export const TAKE_RATE_EXCEPTION_MAX_APPROVED_BY_LENGTH = 255;

export type TakeRateExceptionFormValues = {
  takeRateExceptionJustification?: string;
  takeRateExceptionApprovedBy?: string;
  takeRateExceptionApprovedAt?: string;
};

export type TakeRateExceptionPayload = {
  takeRateException?: {
    justification: string;
    approvedBy: string;
    approvedAt: string;
  };
};

export const isTakeRateException = (
  takeRatePercentage?: number | string | null,
): boolean => {
  if (takeRatePercentage === null || takeRatePercentage === undefined) {
    return false;
  }

  // Number("") é 0, não NaN — sem esta checagem, um campo vazio (string)
  // passaria a se comportar como "take rate 0", que agora é tratado como
  // exceção.
  if (typeof takeRatePercentage === "string" && takeRatePercentage.trim() === "") {
    return false;
  }

  const value = Number(takeRatePercentage);

  // `< 0`, não `<= 0`: 0% é uma taxa válida que o backend também trata como
  // exceção (requiresTakeRateException considera qualquer número < 10,
  // incluindo 0). Tratar 0 como "não é exceção" aqui deixava o formulário
  // validar sem pedir justificativa e o payload seguir sem
  // takeRateException — a API então recusava com 400/4014 sem o operador
  // entender por quê. Negativo é bloqueado antes, no schema (.min(0)).
  if (!Number.isFinite(value) || value < 0) {
    return false;
  }

  return value < TAKE_RATE_EXCEPTION_THRESHOLD;
};

/**
 * Converte a data escolhida no formulário (yyyy-MM-dd) para ISO-8601. Valores
 * já em ISO-8601 são mantidos; valores inválidos viram string vazia, para que a
 * validação do formulário — e não o adapter — seja quem barra o envio.
 */
export const toIsoApprovedAt = (approvedAt?: string | null): string => {
  const value = approvedAt?.trim();

  if (!value) {
    return "";
  }

  // "Z" força meia-noite UTC: sem ela, "yyyy-MM-ddT00:00:00" é interpretado
  // como horário local do runtime (navegador ou CI), e num fuso a leste de
  // UTC isso regride a data em um dia. parseISO (date-fns) tem o mesmo viés
  // para string sem fuso, então não resolve — precisa do "Z" explícito.
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00Z`)
    : new Date(value);

  return isNaN(parsed.getTime()) ? "" : parsed.toISOString();
};

/**
 * Monta o trecho do payload com o objeto da exceção comercial. Retorna um
 * objeto vazio quando o take rate não é uma exceção, para que o comportamento
 * dos contratos com take rate maior ou igual a 10% siga inalterado.
 */
export const buildTakeRateExceptionPayload = (data: {
  takeRatePercentage?: number | string | null;
  takeRateExceptionJustification?: string | null;
  takeRateExceptionApprovedBy?: string | null;
  takeRateExceptionApprovedAt?: string | null;
}): TakeRateExceptionPayload => {
  if (!isTakeRateException(data.takeRatePercentage)) {
    return {};
  }

  return {
    [TAKE_RATE_EXCEPTION_API_FIELD]: {
      [TAKE_RATE_EXCEPTION_API_FIELDS.justification]:
        data.takeRateExceptionJustification?.trim() ?? "",
      [TAKE_RATE_EXCEPTION_API_FIELDS.approvedBy]:
        data.takeRateExceptionApprovedBy?.trim() ?? "",
      [TAKE_RATE_EXCEPTION_API_FIELDS.approvedAt]: toIsoApprovedAt(
        data.takeRateExceptionApprovedAt,
      ),
    },
  };
};
